import { UserPlus, FolderPlus, Send, DollarSign } from 'lucide-react';

const steps = [
  {
    icon: UserPlus,
    number: '01',
    title: 'Sign Up in 60 Seconds',
    desc: 'Create your account, set up your company profile and branding. No credit card required.',
  },
  {
    icon: FolderPlus,
    number: '02',
    title: 'Create Your First Job',
    desc: 'Add a job, build an estimate with line items, and generate a proposal. Send it to your client for approval.',
  },
  {
    icon: Send,
    number: '03',
    title: 'Manage the Project',
    desc: 'Track progress, log change orders, dispatch subs through the portal, and keep field crews in sync — all from one dashboard.',
  },
  {
    icon: DollarSign,
    number: '04',
    title: 'Get Paid Faster',
    desc: 'Send invoices, collect lien waivers before releasing payment, and close out jobs clean with a full paper trail.',
  },
];

export function HowItWorksSection() {
  return (
    <section id="how-it-works" className="relative py-28 sm:py-32">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-16 text-center reveal">
          <span className="mb-4 inline-block rounded-full border border-build-700/40 bg-build-900/40 px-3 py-1 text-xs font-medium text-orange-400">
            How It Works
          </span>
          <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Up and Running in Minutes
          </h2>
          <p className="mx-auto max-w-2xl text-zinc-400">
            No complex setup. No consultants. Just sign up and start managing your jobs like a pro.
          </p>
        </div>

        {/* Connector line — desktop only. Positioned absolutely above step circles */}
        <div className="relative">
          <div className="pointer-events-none absolute left-0 right-0 top-7 hidden lg:block" aria-hidden="true">
            <svg
              className="mx-auto h-4 w-full max-w-5xl"
              viewBox="0 0 1000 16"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="step-line-grad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#f97316" stopOpacity="0.1" />
                  <stop offset="12%" stopColor="#f97316" stopOpacity="0.5" />
                  <stop offset="38%" stopColor="#fbbf24" stopOpacity="0.5" />
                  <stop offset="62%" stopColor="#fbbf24" stopOpacity="0.5" />
                  <stop offset="88%" stopColor="#f97316" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#f97316" stopOpacity="0.1" />
                </linearGradient>
              </defs>
              {/* Dashed blueprint path between the 4 circles */}
              <line
                x1="125" y1="8" x2="875" y2="8"
                stroke="url(#step-line-grad)"
                strokeWidth="2"
                strokeDasharray="6 6"
                strokeLinecap="round"
              />
              {/* Tick dots marking each stop */}
              <circle cx="125" cy="8" r="3" fill="#f97316" />
              <circle cx="375" cy="8" r="3" fill="#fbbf24" />
              <circle cx="625" cy="8" r="3" fill="#fbbf24" />
              <circle cx="875" cy="8" r="3" fill="#f97316" />
            </svg>
          </div>

          <div className="relative grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step) => (
              <div key={step.number} className="reveal relative text-center">
                <div
                  className="relative mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full ring-4 ring-[#111111]"
                  style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
                >
                  <step.icon className="h-6 w-6 text-white" />
                </div>
                <div className="mb-2 text-xs font-bold text-orange-500/60 tracking-widest">STEP {step.number}</div>
                <h3 className="mb-2 text-base font-semibold">{step.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-400">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
